import {
  Modal,
  TextInput,
  Stack,
  Checkbox,
  Button,
  Flex,
  Text,
} from "@mantine/core";
import { useState, useMemo, useCallback } from "react";
import type { ConverterProps, ConverterRow } from "./CustomConverter";
import { useFormContext } from "react-hook-form";
import { usePostApi } from "src/hooks";
import { getYachtId } from "src/utils";
import { useParams } from "react-router-dom";
interface ConverterModalProps {
  onClose: () => void;
  converterProps?: ConverterProps;
}

interface RowState {
  [fieldIndex: number]: string;
}

interface ConversionRowUI extends ConverterRow {
  rowIndex: number;
  values: RowState;
}

export default function ConverterModal({
  onClose,
  converterProps,
}: ConverterModalProps) {
  const { post } = usePostApi();
  const [allRowValues, setAllRowValues] = useState<Record<number, RowState>>(
    {},
  );
  const [selectedRow, setSelectedRow] = useState<number | null>(0);
  const { setValue } = useFormContext();
  const id = converterProps?.fieldId;

  const { lbeidOrPartId, lbid } = useParams();

  const recordHistoryAction = useCallback(
    async (action: string) => {
      try {
        await post({
          request: "record-history-action",
          body: {
            params: {
              yachtid: getYachtId(),
              lbid,
              lbeid: lbeidOrPartId,
              action,
            },
          },
        });
      } catch (error) {}
    },
    [post],
  );

  const conversionRows = useMemo((): ConversionRowUI[] => {
    if (!converterProps?.rows || !Array.isArray(converterProps.rows)) {
      return [];
    }

    return converterProps.rows.map((row, rowIndex) => ({
      ...row,
      rowIndex,
      values: allRowValues[rowIndex] || {},
    }));
  }, [converterProps?.rows, allRowValues]);

  const calculateResult = useCallback(
    (rowIndex: number): string => {
      const row = converterProps?.rows?.[rowIndex];
      if (!row || !row.fields) return "";

      const values = allRowValues[rowIndex] || {};
      const fieldValues = Object.values(values);

      if (
        fieldValues.length !== row.fields.length ||
        fieldValues.some(v => !v)
      ) {
        return "";
      }

      const nums = fieldValues
        .map((v, idx) => {
          const num = parseFloat(v);
          if (isNaN(num)) return NaN;

          const divisor = row.divisors?.[idx] ?? 1;
          return num / divisor;
        })
        .filter(n => !isNaN(n));

      if (nums.length !== row.fields.length) return "";

      let result = nums[0];
      for (let i = 1; i < nums.length; i++) {
        result =
          converterProps?.type === "divide"
            ? result / nums[i]
            : result * nums[i];
      }

      return result.toFixed(2);
    },
    [allRowValues, converterProps],
  );

  const handleFieldChange = (
    rowIndex: number,
    fieldIndex: number,
    value: string,
  ) => {
    setAllRowValues(prev => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [fieldIndex]: value,
      },
    }));
  };

  const getUnit = (label: string): string => {
    return label.slice(label.indexOf("[") + 1, label.indexOf("]"));
  };

  const getEquation = (row: ConversionRowUI): string => {
    return `${row.result} = ${row.fields.map((f, i) => (row.divisors?.[i] !== 1 ? `( ${f.slice(0, f.indexOf("["))} / ${row.divisors?.[i]} ${f.slice(f.indexOf("[", -1))} )` : f)).join(converterProps?.type === "divide" ? " ÷ " : " × ")}`;
  };

  const handleDone = () => {
    if (selectedRow !== null) {
      onClose();
      const value = calculateResult(selectedRow);
      if (id && value && selectedRow !== null) {
        const equation = getEquation(conversionRows[selectedRow]);
        setValue(id, value);
        recordHistoryAction(`User Selected ${equation}`);
      }
    }
  };

  return (
    <Modal opened title="Converter" onClose={onClose} size={"50%"} centered>
      <Stack spacing="xl">
        {conversionRows.map(row => (
          <Flex key={row.rowIndex} direction="column" gap={4}>
            <Text size={14} pl={36 + 16}>
              {getEquation(row)}
            </Text>
            <Flex
              key={row.rowIndex}
              gap="md"
              align="center"
              onClick={() => {
                if (selectedRow === row.rowIndex) return;
                setSelectedRow(row.rowIndex);
                setAllRowValues({});
              }}
              style={{
                opacity: selectedRow === row.rowIndex ? 1 : 0.5,
                cursor: "pointer",
              }}>
              <Checkbox
                checked={selectedRow === row.rowIndex}
                style={{
                  padding: 8,
                  pointerEvents: "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                styles={{ input: { cursor: "pointer" } }}
              />
              {row.fields.map((fieldLabel, fieldIndex) => (
                <TextInput
                  key={fieldIndex}
                  value={row.values[fieldIndex] || ""}
                  rightSection={getUnit(fieldLabel)}
                  rightSectionProps={{
                    style: {
                      fontSize: 14,
                      width: "max-content",
                      padding: "0 10px",
                    },
                  }}
                  onChange={e =>
                    handleFieldChange(
                      row.rowIndex,
                      fieldIndex,
                      e.currentTarget.value,
                    )
                  }
                  type="number"
                  style={{
                    flex: 1,
                  }}
                />
              ))}
              <Text px={16}>Total</Text>
              <TextInput
                value={calculateResult(row.rowIndex)}
                rightSection={getUnit(row.result)}
                readOnly
                style={{ flex: 1, pointerEvents: "none" }}
              />
            </Flex>
          </Flex>
        ))}
        <Flex justify="center" gap="md" mt="lg">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleDone}
            disabled={selectedRow === null || !calculateResult(selectedRow)}>
            Done
          </Button>
        </Flex>
      </Stack>
    </Modal>
  );
}
